import * as Phaser from "phaser"
import { EventBus } from "../core/event-bus"
import { GameState } from "../core/game-state"
import { getHeroColorTheme } from "../core/hero-colors"
import { InputManager } from "../core/input-mapper"
import { EVENT, SCENE } from "../core/keys"
import { THEME } from "../core/theme"
import { playConfirm, playHover } from "../core/ui-sfx"
import { GAME_HEIGHT, GAME_WIDTH } from "../data/constants"
import type { Player, StatUpgrade, Upgrade } from "../types"
import { NeonPanel } from "../ui/components"
import {
  applyModalCameraFx,
  clearCameraFx,
  drawFilterNeonBorder,
  drawNeonGrid,
  drawNeonLine,
  drawScanlines,
  neonTextShadow,
} from "../ui/neon-fx"
import { getWaveRuntime } from "./wave-runtime-api"

const FONT = THEME.fonts
const COLOR = THEME.colors

const CARD_W = 190
const CARD_H = 300
const CARD_GAP = 24
const STATS_W = 240
const STATS_H = CARD_H
const PANEL_GAP = 40

function displayStatLevel(level: number): number {
  return level + 1
}

interface UpgradeChoiceData {
  wave: number
  choices: Upgrade[]
}

export class UpgradeChoiceScene extends Phaser.Scene {
  private choices: Upgrade[] = []
  private player!: Player
  private waveCamera?: Phaser.Cameras.Scene2D.Camera
  private selectedIndex = 0
  private cards: NeonPanel[] = []
  private inputManager!: InputManager
  private statsLabels: Map<string, Phaser.GameObjects.Text> = new Map()
  private diffLabels: Map<string, Phaser.GameObjects.Text> = new Map()
  private levelLabels: Map<string, Phaser.GameObjects.Text> = new Map()

  constructor() {
    super({ key: SCENE.UPGRADE_CHOICE })
  }

  init(data: UpgradeChoiceData): void {
    this.choices = data.choices ?? []
    this.player =
      getWaveRuntime(this)?.getGameData().player ??
      GameState.instance.player ??
      ({} as Player)
    this.selectedIndex = 0
    this.cards = []
    this.statsLabels.clear()
    this.diffLabels.clear()
    this.levelLabels.clear()
  }

  create(): void {
    this.inputManager = new InputManager(this, {
      onDown: (action, event) => {
        // Ignore global pointer events for these actions in the menu
        if (event?.pointer && (action === "CONFIRM" || action === "FLAP"))
          return

        switch (action) {
          case "LEFT":
            this.moveSelection(-1)
            break
          case "RIGHT":
            this.moveSelection(1)
            break
          case "CONFIRM":
          case "FLAP":
            this.confirmSelection()
            break
        }
      },
    })

    // Listen for 'R' key for reroll
    this.input.keyboard?.on("keydown-R", () => this.handleReroll())

    if (this.scene.isActive(SCENE.WAVE) || this.scene.isPaused(SCENE.WAVE)) {
      this.waveCamera = this.scene.get(SCENE.WAVE).cameras.main
      applyModalCameraFx(this.waveCamera, {
        blurStrength: 2,
        blurSteps: 6,
        vignetteRadius: 0.5,
        vignetteStrength: 0.7,
      })
    }

    this.renderScene()

    this.events.once("shutdown", () => {
      this.tweens.killAll()
      this.inputManager.destroy()
      if (this.waveCamera) {
        clearCameraFx(this.waveCamera)
        this.waveCamera = undefined
      }
    })

    this.cameras.main.setAlpha(0)
    this.tweens.add({
      targets: this.cameras.main,
      alpha: 1,
      duration: 250,
      ease: "Quad.easeOut",
    })
  }

  private addFittedCardText(
    x: number,
    y: number,
    text: string,
    config: Phaser.Types.GameObjects.Text.TextStyle,
    maxWidth: number,
    maxHeight: number,
    originX = 0.5,
    originY = 0.5,
  ): Phaser.GameObjects.Text {
    const resolved = {
      ...config,
      wordWrap: { width: maxWidth },
      align: "center",
    }
    const label = this.add
      .text(x, y, text, resolved)
      .setOrigin(originX, originY)
    const baseFontSize =
      Number.parseInt(String(config.fontSize ?? "10"), 10) || 10

    for (let size = baseFontSize; size >= 8; size -= 1) {
      label.setFontSize(size)
      label.setWordWrapWidth(maxWidth, true)
      if (label.width <= maxWidth && label.height <= maxHeight) {
        break
      }
    }

    return label
  }

  private renderScene(): void {
    const cx = GAME_WIDTH / 2
    const cy = GAME_HEIGHT / 2

    this.children.removeAll()
    this.cards = []

    // ── Dark overlay ──────────────────────────────────────────────
    this.add.rectangle(cx, cy, GAME_WIDTH, GAME_HEIGHT, COLOR.black, 0.85)

    // ── Background Grid ───────────────────────────────────────────
    const heroId = GameState.instance.character?.id ?? "bella"
    const heroColors = getHeroColorTheme(heroId)
    drawNeonGrid(this, 0, 0, GAME_WIDTH, GAME_HEIGHT, heroColors.neon, 0.08, 60)
    drawScanlines(this, 0, 0, GAME_WIDTH, GAME_HEIGHT, 0.04, 3)

    // ── Title ─────────────────────────────────────────────────────
    this.add
      .text(cx, 60, "LEVEL UP!", {
        fontSize: "48px",
        fontFamily: FONT.sans,
        fontStyle: "900",
        color: heroColors.glowText,
        shadow: neonTextShadow(heroColors.glowShadow, 20),
      })
      .setOrigin(0.5)

    this.add
      .text(
        cx,
        95,
        `WAVE ${getWaveRuntime(this)?.getGameData().wave ?? 0} COMPLETED`,
        {
          fontSize: "14px",
          fontFamily: FONT.pixel,
          color: COLOR.mutedText,
          letterSpacing: 2,
        },
      )
      .setOrigin(0.5)

    const accentLine = this.add.graphics()
    drawNeonLine(
      accentLine,
      cx - 200,
      115,
      cx + 200,
      heroColors.neon,
      0.6,
      0.15,
      2,
      10,
    )

    // ── Layout Calculations ───────────────────────────────────────
    const totalW =
      STATS_W +
      PANEL_GAP +
      (this.choices.length * CARD_W + (this.choices.length - 1) * CARD_GAP)
    const startX = cx - totalW / 2
    const panelY = 160

    // ── Stats Panel ───────────────────────────────────────────────
    this.renderStatsPanel(startX, panelY)

    // ── Choice Cards ──────────────────────────────────────────────
    const cardsX = startX + STATS_W + PANEL_GAP
    this.choices.forEach((choice, i) => {
      const bx = cardsX + i * (CARD_W + CARD_GAP)
      this.renderCard(bx, panelY, choice, i)
    })

    // ── Reroll Button ──────────────────────────────────────────────
    this.renderRerollButton(cx, panelY + CARD_H + 40)

    this.updateSelection()
  }

  private renderRerollButton(x: number, y: number): void {
    const rerollCount = this.player.rerollCharges
    const hasFreeReroll = rerollCount > 0
    const rerollColor = hasFreeReroll ? COLOR.success : 0x9ca3af

    new NeonPanel(this, {
      x: x - 80,
      y: y - 20,
      width: 160,
      height: 40,
      color: rerollColor,
      alpha: 0.8,
      showInner: true,
    })

    this.add
      .text(x, y, `REROLL (${hasFreeReroll ? "FREE" : "N/A"})`, {
        fontSize: "14px",
        fontFamily: FONT.pixel,
        color: hasFreeReroll
          ? THEME.colors.whiteTextSoft
          : THEME.colors.mutedText,
      })
      .setOrigin(0.5)

    if (hasFreeReroll) {
      const hitZone = this.add
        .zone(x, y, 160, 40)
        .setInteractive({ useHandCursor: true })
      hitZone.on("pointerdown", () => this.handleReroll())

      this.add
        .text(x, y + 25, "PRESS [R] TO REROLL", {
          fontSize: "10px",
          fontFamily: FONT.pixel,
          color: COLOR.mutedText,
        })
        .setOrigin(0.5)
    }
  }

  private handleReroll(): void {
    if (this.player.rerollCharges <= 0) return

    this.player.rerollCharges -= 1
    playConfirm(this)

    const waveRuntime = getWaveRuntime(this)
    if (waveRuntime) {
      const newChoices = waveRuntime.getUpgradeChoices()
      this.choices = newChoices
      this.renderScene()
    }
  }

  private renderStatsPanel(x: number, y: number): void {
    new NeonPanel(this, {
      x,
      y,
      width: STATS_W,
      height: STATS_H,
      color: 0x1a2040,
      alpha: 0.8,
      showInner: true,
    })

    this.add
      .text(x + STATS_W / 2, y + 25, "CURRENT STATS", {
        fontSize: "14px",
        fontFamily: FONT.pixel,
        color: COLOR.whiteTextSoft,
      })
      .setOrigin(0.5)

    const stats = this.player.stats
    const levels = this.player.statLevels || {}
    const statEntries = [
      { key: "maxHp", levelKey: "hp", label: "MAX HP", value: stats.maxHp },
      {
        key: "attackSpeed",
        levelKey: "attackSpeed",
        label: "ATK SPEED",
        value: stats.attackSpeed,
        isPct: true,
      },
      {
        key: "flapPower",
        levelKey: "flapPower",
        label: "FLAP FORCE",
        value: stats.flapPower,
        isPct: true,
      },
      {
        key: "ballCount",
        levelKey: "ballCount",
        label: "BALLS",
        value: stats.ballCount,
      },
      {
        key: "ballBounces",
        levelKey: "ballBounces",
        label: "BOUNCES",
        value: stats.ballBounces,
      },
      {
        key: "ballSize",
        levelKey: "ballSize",
        label: "BALL POWER",
        value: stats.ballSize,
        isPct: true,
      },
    ]

    statEntries.forEach((s, i) => {
      const sy = y + 70 + i * 45

      this.add.text(x + 20, sy, s.label, {
        fontSize: "10px",
        fontFamily: FONT.pixel,
        color: COLOR.mutedText,
      })

      const level = levels[s.levelKey] || 0
      const levelLabel = this.add.text(
        x + 20,
        sy + 15,
        `Lv ${displayStatLevel(level)}`,
        {
          fontSize: "9px",
          fontFamily: FONT.pixel,
          color: `#${COLOR.info.toString(16).padStart(6, "0")}`,
        },
      )
      this.levelLabels.set(s.key, levelLabel)

      const valStr = s.isPct
        ? `${Math.round(s.value * 100)}%`
        : s.value.toString()
      const valLabel = this.add
        .text(x + STATS_W - 20, sy, valStr, {
          fontSize: "11px",
          fontFamily: FONT.pixel,
          color: COLOR.whiteTextSoft,
        })
        .setOrigin(1, 0)

      this.statsLabels.set(s.key, valLabel)

      const diffLabel = this.add
        .text(x + STATS_W - 20, sy + 15, "", {
          fontSize: "9px",
          fontFamily: FONT.pixel,
          color: `#${COLOR.success.toString(16).padStart(6, "0")}`,
        })
        .setOrigin(1, 0)
        .setVisible(false)

      this.diffLabels.set(s.key, diffLabel)
    })
  }

  private renderCard(
    x: number,
    y: number,
    choice: Upgrade,
    index: number,
  ): void {
    const rarityColor = this.getRarityColor(choice.rarity)

    const card = new NeonPanel(this, {
      x,
      y,
      width: CARD_W,
      height: CARD_H,
      color: rarityColor,
      alpha: 0.9,
      showInner: true,
      glowStrength: 0,
    })

    // Rarity Header
    const headerH = 30
    const header = this.add.graphics()
    header.fillStyle(rarityColor, 0.2)
    header.fillRoundedRect(x + 5, y + 5, CARD_W - 10, headerH, 4)

    this.add
      .text(x + CARD_W / 2, y + 5 + headerH / 2, choice.rarity.toUpperCase(), {
        fontSize: "9px",
        fontFamily: FONT.pixel,
        color:
          Phaser.Display.Color.IntegerToColor(rarityColor).lighten(20).rgba,
        letterSpacing: 2,
      })
      .setOrigin(0.5)

    // Name
    const displayName = this.getUpgradeDisplayName(choice)
    this.addFittedCardText(
      x + CARD_W / 2,
      y + 50,
      displayName.toUpperCase(),
      {
        fontSize: "14px",
        fontFamily: FONT.sans,
        fontStyle: "900",
        color: "#ffffff",
      },
      CARD_W - 24,
      38,
    )

    // Icon
    if (choice.iconTextureKey) {
      const icon = this.add.sprite(
        x + CARD_W / 2,
        y + 110,
        choice.iconTextureKey,
      )
      icon.setDisplaySize(64, 64)
    } else {
      this.add
        .text(x + CARD_W / 2, y + 110, choice.icon || "?", {
          fontSize: "48px",
          fontFamily: FONT.pixel,
        })
        .setOrigin(0.5)
    }

    // Effect
    const effectText = this.getEffectLabel(choice)
    if (effectText) {
      this.addFittedCardText(
        x + CARD_W / 2,
        y + 180,
        effectText,
        {
          fontSize: "12px",
          fontFamily: FONT.pixel,
          color: `#${rarityColor.toString(16).padStart(6, "0")}`,
        },
        CARD_W - 30,
        32,
      )
    }

    // Description
    this.addFittedCardText(
      x + CARD_W / 2,
      y + 214,
      choice.description,
      {
        fontSize: "10px",
        fontFamily: FONT.pixel,
        color: COLOR.mutedText,
      },
      CARD_W - 30,
      72,
      0.5,
      0,
    )

    // Interaction
    const hitZone = this.add
      .zone(x + CARD_W / 2, y + CARD_H / 2, CARD_W, CARD_H)
      .setInteractive({ useHandCursor: true })

    hitZone.on("pointerover", () => {
      if (this.selectedIndex !== index) {
        this.selectedIndex = index
        this.updateSelection()
        playHover(this)
      }
    })

    hitZone.on("pointerdown", () => {
      if (this.selectedIndex !== index) {
        this.selectedIndex = index
        this.updateSelection()
        playHover(this)
      } else {
        this.confirmSelection()
      }
    })

    this.cards.push(card)

    // Entrance animation
    card.y += 50
    card.alpha = 0
    this.tweens.add({
      targets: card,
      y: y,
      alpha: 1,
      duration: 400,
      delay: index * 100,
      ease: "Back.easeOut",
    })
  }

  private updateSelection(): void {
    this.cards.forEach((card, i) => {
      const isSelected = i === this.selectedIndex
      const choice = this.choices[i]
      const rarityColor = this.getRarityColor(choice.rarity)

      const border = card.getBorderGraphics()
      border.clear()

      if (isSelected) {
        drawFilterNeonBorder(
          border,
          0,
          0,
          CARD_W,
          CARD_H,
          rarityColor,
          6,
          1,
          10,
        )
        this.tweens.add({
          targets: card,
          scale: 1.05,
          duration: 200,
          ease: "Quad.easeOut",
        })
      } else {
        border.lineStyle(1.5, rarityColor, 0.3)
        border.strokeRoundedRect(0, 0, CARD_W, CARD_H, 6)
        this.tweens.add({
          targets: card,
          scale: 1,
          duration: 200,
          ease: "Quad.easeOut",
        })
      }
    })

    this.updateStatsComparison()
  }

  private updateStatsComparison(): void {
    const choice = this.choices[this.selectedIndex]

    // Reset diff labels
    this.diffLabels.forEach((label) => label.setVisible(false))

    if (!choice) return

    if (choice.type === "stat" && (choice as StatUpgrade).statBoost) {
      const boosts = (choice as StatUpgrade).statBoost
      Object.entries(boosts).forEach(([key]) => {
        const label = this.diffLabels.get(key)
        if (label) {
          const levelKey = key === "maxHp" ? "hp" : key
          const currentLevel = this.player.statLevels?.[levelKey] || 0
          const nextLevel = currentLevel + 1

          let diffStr = ""
          if (key === "maxHp") {
            let ratio = 0
            if (nextLevel === 1) ratio = 0.8
            else if (nextLevel === 2) ratio = 0.5
            else if (nextLevel === 3) ratio = 0.25
            else ratio = 0.1
            diffStr = `+${Math.round(ratio * 100)}%`
          } else if (
            key === "attackSpeed" ||
            key === "ballSize" ||
            key === "flapPower"
          ) {
            diffStr = "+10%"
          } else {
            diffStr = "+1"
          }

          label.setText(diffStr).setVisible(true)

          // Flash the main stat label
          const mainLabel = this.statsLabels.get(key)
          if (mainLabel) {
            this.tweens.add({
              targets: mainLabel,
              scale: 1.2,
              duration: 100,
              yoyo: true,
              ease: "Quad.easeOut",
            })
          }
        }
      })
    }
  }

  private moveSelection(dir: number): void {
    this.selectedIndex =
      (this.selectedIndex + dir + this.choices.length) % this.choices.length
    this.updateSelection()
    playHover(this)
  }

  private confirmSelection(): void {
    const choice = this.choices[this.selectedIndex]
    if (!choice) return
    playConfirm(this)

    // Card punch animation
    const selectedCard = this.cards[this.selectedIndex]
    this.tweens.add({
      targets: selectedCard,
      scale: 1.2,
      alpha: 0,
      duration: 300,
      ease: "Expo.easeOut",
      onComplete: () => this.handleChoose(choice),
    })

    // Fade out others
    this.cards.forEach((card, i) => {
      if (i !== this.selectedIndex) {
        this.tweens.add({
          targets: card,
          scale: 0.8,
          alpha: 0,
          duration: 200,
          ease: "Quad.easeIn",
        })
      }
    })
  }

  private getRarityColor(rarity?: string): number {
    switch (rarity) {
      case "uncommon":
        return 0x22c55e
      case "rare":
        return 0x3b82f6
      case "legendary":
        return 0xf59e0b
      default:
        return 0x9ca3af
    }
  }

  private getEffectLabel(upgrade: Upgrade): string {
    if (upgrade.type === "stat" && upgrade.statBoost) {
      const key = Object.keys(upgrade.statBoost)[0]
      const levelKey = key === "maxHp" ? "hp" : key
      const currentLevel = this.player.statLevels?.[levelKey] || 0
      const nextLevel = displayStatLevel(currentLevel + 1)

      return `LEVEL ${nextLevel}`
    }

    return ""
  }

  private getUpgradeDisplayName(upgrade: Upgrade): string {
    if (upgrade.type === "stat" && upgrade.statBoost) {
      const key = Object.keys(upgrade.statBoost)[0]
      const labelMap: Record<string, string> = {
        maxHp: "HP",
        attackSpeed: "SPEED",
        ballCount: "BALLS",
        ballBounces: "BOUNCES",
        ballSize: "POWER",
        flapPower: "FLAP",
      }

      return `${labelMap[key] || "UPGRADE"}`
    }
    return upgrade.name
  }

  private handleChoose(upgrade: Upgrade): void {
    const waveRuntime = getWaveRuntime(this)
    if (waveRuntime) {
      waveRuntime.applyUpgradeChoice(upgrade)
    } else {
      EventBus.emit(EVENT.UPGRADE_CHOSEN, upgrade)
      this.scene.stop(SCENE.UPGRADE_CHOICE)
    }
  }
}
